"use client"

import { motion, useInView } from "motion/react"
import Image from "next/image"
import { useRef } from "react"
import { BorderBeam } from "@/components/magicui/boarder-beam"
import { config } from "@/config"
import { cn } from "@/utils/cn"

interface HeroDashboardPreviewProps {
  className?: string
}

function HeroDashboardPreview({ className }: HeroDashboardPreviewProps) {
  return (
    <div
      className={cn(
        "relative h-full w-full rounded-xl after:absolute after:inset-0 after:z-10 after:[background:linear-gradient(to_top,#fff_30%,transparent)] dark:after:[background:linear-gradient(to_top,#000000_30%,transparent)]",
        className,
      )}
    >
      <div
        className={cn(
          "absolute inset-0 bottom-1/2 h-full w-full transform-gpu [filter:blur(120px)]",
          // light styles
          "[background-image:linear-gradient(to_bottom,#ffaa40,transparent_30%)]",
          // dark styles
          "dark:[background-image:linear-gradient(to_bottom,#ffffff,transparent_30%)]",
        )}
      />

      <Image
        width={2672}
        height={1600}
        alt={config.site.name}
        src="/landing/dashboard-light.png"
        className="relative block h-full w-full rounded-xl dark:hidden"
      />
      <Image
        width={3276}
        height={1766}
        alt={config.site.name}
        src="/landing/dashboard-dark.png"
        className="relative hidden h-full w-full rounded-xl dark:block"
      />

      <BorderBeam size={150} />
      <BorderBeam size={150} delay={6} />
    </div>
  )
}

export function HeroPreview() {
  const fadeInRef = useRef(null)
  const fadeInInView = useInView(fadeInRef, {
    once: true,
  })

  const fadeUpVariants = {
    initial: {
      opacity: 0,
      y: 24,
    },
    animate: {
      opacity: 1,
      y: 0,
    },
  }

  return (
    <div id="dashboard-preview">
      <motion.div
        ref={fadeInRef}
        className="relative mx-auto flex w-full select-none items-center justify-center"
        animate={fadeInInView ? "animate" : "initial"}
        variants={fadeUpVariants}
        initial={false}
        transition={{
          duration: 1.4,
          delay: 0.4,
          ease: [0.21, 0.47, 0.32, 0.98],
          type: "spring",
        }}
      >
        <HeroDashboardPreview className="h-full max-w-6xl rounded-lg border" />
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-linear-to-b from-background/0 via-background/0 to-background" />
      </motion.div>
    </div>
  )
}

export default HeroDashboardPreview
